// Thin fetch layer. Session-cookie auth (credentials: include). Django REST's
// SessionAuthentication enforces CSRF on unsafe methods, so we send the
// X-CSRFToken from the csrftoken cookie on POST/PUT/PATCH/DELETE. A 403 is NOT
// treated as "logged out" — that's the MeProvider's job (it gates on /api/me);
// a 403 here is a CSRF/permission error and is surfaced to the caller.
const BASE = "/api";

function cookie(name) {
  const m = document.cookie.match("(?:^|; )" + name + "=([^;]*)");
  return m ? decodeURIComponent(m[1]) : "";
}

export async function api(path, opts = {}) {
  const method = (opts.method || "GET").toUpperCase();
  const headers = { "Content-Type": "application/json", ...(opts.headers || {}) };
  if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
    const csrf = cookie("csrftoken");
    if (csrf) headers["X-CSRFToken"] = csrf;
  }
  const res = await fetch(`${BASE}/${path.replace(/^\//, "")}`, {
    credentials: "include", headers, ...opts,
  });
  if (!res.ok) {
    const err = new Error(`${res.status} ${res.statusText}`);
    err.status = res.status;
    // Attach the parsed error body so callers can surface a server-sent
    // {error, note, detail} (e.g. the order endpoint's validation messages).
    try {
      const ect = res.headers.get("content-type") || "";
      err.body = ect.includes("application/json") ? await res.json() : await res.text();
    } catch { /* body unreadable — keep the status-only message */ }
    throw err;
  }
  const ct = res.headers.get("content-type") || "";
  return ct.includes("application/json") ? res.json() : res.text();
}

// Streaming POST (Server-Sent Events). Invokes onEvent(eventName, data) for each `event:/data:`
// frame as it arrives over the wire, so the UI can render tokens live. Same session-cookie + CSRF
// auth as api(). Resolves when the stream ends; rejects on a non-OK status (err.status set).
export async function streamSSE(path, body, onEvent, signal) {
  const csrf = cookie("csrftoken");
  const res = await fetch(`${BASE}/${path.replace(/^\//, "")}`, {
    method: "POST", credentials: "include", signal,
    headers: { "Content-Type": "application/json", ...(csrf ? { "X-CSRFToken": csrf } : {}) },
    body: JSON.stringify(body || {}),
  });
  if (!res.ok || !res.body) {
    const err = new Error(`${res.status} ${res.statusText}`); err.status = res.status; throw err;
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    // The AbortController only cancels the INITIAL fetch, not this read loop — so after the modal
    // closes (signal aborted) a billed Anthropic thesis stream would otherwise drain to completion.
    // Check the signal each iteration and cancel the reader to stop the spend immediately.
    if (signal?.aborted) { try { await reader.cancel(); } catch { /* already closed */ } break; }
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    let i;
    while ((i = buf.indexOf("\n\n")) >= 0) {
      const frame = buf.slice(0, i); buf = buf.slice(i + 2);
      let ev = "message", data = "";
      for (const line of frame.split("\n")) {
        if (line.startsWith("event:")) ev = line.slice(6).trim();
        else if (line.startsWith("data:")) data += line.slice(5).trim();
      }
      if (data) { let parsed; try { parsed = JSON.parse(data); } catch { parsed = data; } onEvent(ev, parsed); }
    }
  }
}

// formatting helpers shared by the (to-be-designed) tables/cards
export const num = (x, d = 2) => (x == null ? "—" : Number(x).toFixed(d));

// UI-9: one timestamp convention — UTC, explicitly labelled. Browser-local
// toLocaleString() with no tz is ambiguous (an audit log especially must be
// unambiguous). Renders "YYYY-MM-DD HH:MM UTC". Returns "—" for empty/invalid.
export const fmtTs = (iso) => {
  if (iso == null) return "—";
  const d = new Date(iso);
  return isNaN(d) ? "—" : d.toISOString().slice(0, 16).replace("T", " ") + " UTC";
};
